import { formatCurrency } from "@/lib/money";

export const TOTAL_QUIZ_STEPS = 2;

// Destination (preferred country/region) filter is paused for now — hidden in
// QuizStepConstraints and QuizStepReview. Flip to true to bring the UI back;
// the underlying data flow (null defaults, PATCH payload) is untouched.
export const SHOW_PREFERRED_DESTINATION = false;

// Budget slider ranges are currency-specific — see CURRENCY_CONFIG below.

export const SUPPORTED_CURRENCIES = ["USD", "EUR", "GBP", "ZAR", "AUD", "CAD"] as const;
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

type CurrencyConfig = {
  min: number;
  max: number;
  step: number;
  // Currently unused by any UI. The budget slider's decorative tick ruler
  // (RangeSlider.tsx showRuler) is intentionally 49 evenly-spaced marks, NOT
  // driven by these 5 labelled stops — see the design handoff at
  // Extra Files/Claude Design - Handoff Files/design_handoff_budget_slider_fade.
  // Kept for a possible future labelled-stops design; don't repurpose it for
  // the ruler without revisiting that decision.
  ticks: readonly number[];
};

// There is deliberately no defaultBudget here: the slider starts unset (null)
// and the user must touch it before continuing — see ConstraintsDraft.budget
// in quizSchemas.ts and RangeSlider's null handling. A default nobody chose
// isn't sent to the backend at all. (Django's budget filter admits
// rate ∈ budget × [0.70, 1.10], see recommendations/filters.py:20-22, which is
// why a per-currency default used to be hand-tuned.)
export const CURRENCY_CONFIG: Record<SupportedCurrency, CurrencyConfig> = {
  USD: { min: 300,   max: 5000,  step: 100,  ticks: [300, 1000, 2000, 3000, 5000]      },
  EUR: { min: 300,   max: 5000,  step: 100,  ticks: [300, 1000, 2000, 3000, 5000]      },
  GBP: { min: 250,   max: 4000,  step: 50,   ticks: [250, 1000, 2000, 3000, 4000]      },
  ZAR: { min: 5000,  max: 90000, step: 1000, ticks: [5000, 20000, 45000, 70000, 90000] },
  AUD: { min: 500,   max: 8000,  step: 100,  ticks: [500, 2000, 4000, 6000, 8000]      },
  CAD: { min: 400,   max: 6500,  step: 100,  ticks: [400, 1500, 3000, 5000, 6500]      },
};

export function isSupportedCurrency(v: unknown): v is SupportedCurrency {
  return typeof v === "string" && (SUPPORTED_CURRENCIES as readonly string[]).includes(v);
}

// What the user's budget number means — mirrors Django's
// QuestionnaireResponse.budget_flexibility. "max" (ceiling
// semantics) is the product default the quiz pre-selects; the backend's own
// model default is "target" (legacy near-the-number band) so payloads that
// omit the field keep the old behavior.
export const BUDGET_FLEXIBILITY_VALUES = ["max", "target"] as const;
export type BudgetFlexibility = (typeof BUDGET_FLEXIBILITY_VALUES)[number];
export const DEFAULT_BUDGET_FLEXIBILITY: BudgetFlexibility = "max";

export function isBudgetFlexibility(v: unknown): v is BudgetFlexibility {
  return (
    typeof v === "string" && (BUDGET_FLEXIBILITY_VALUES as readonly string[]).includes(v)
  );
}

const EUROZONE_REGIONS = new Set([
  "AT","BE","CY","DE","EE","ES","FI","FR","GR","HR",
  "IE","IT","LT","LU","LV","MT","NL","PT","SI","SK",
]);

function detectCurrency(): SupportedCurrency {
  if (typeof navigator === "undefined") return "USD";
  const lang = navigator.language ?? "";
  const region = lang.split("-")[1]?.toUpperCase() ?? "";
  if (region === "ZA") return "ZAR";
  if (region === "AU") return "AUD";
  if (region === "CA") return "CAD";
  if (region === "GB") return "GBP";
  if (EUROZONE_REGIONS.has(region)) return "EUR";
  return "USD";
}

// Versioned (.v2): pre-TRA-300, this key was written unconditionally on every render
// — including the very first, with the SSR "USD" default — so a value under the old,
// unversioned key is indistinguishable from the bug this key exists to fix and must
// never be trusted. Bumping it means every pre-fix browser gets one clean re-detect
// instead of staying pinned to a clobbered currency forever.
const STORAGE_KEY = "bsa.quiz.budgetCurrency.v2";

function readStoredCurrency(): SupportedCurrency | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isSupportedCurrency(v) ? v : null;
  } catch { return null; }
}

export function writeStoredCurrency(c: SupportedCurrency): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE_KEY, c); } catch { /* ignore quota / privacy errors */ }
}

export function resolveInitialCurrency(): SupportedCurrency {
  return readStoredCurrency() ?? detectCurrency();
}

export function formatBudget(amount: number, currency: SupportedCurrency): string {
  return formatCurrency(amount, currency);
}
